import * as React from "react";
import {Grid2 as Grid, InputAdornment} from "@mui/material";
import {OrderDataDTO} from "@/api/data-contracts";
import {ChangeHandler} from "@/lib/modelstate";
import {ErrorsShape} from "@/lib/utils";
import {CardEx} from "@/lib/components/CardEx";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import {numberOfPalletLabels, palletTypeOptions} from "@/datacaches";
import NumberInputField from "@/lib/components/inputfields/number-input-field";
import InfoTooltipIcon from "@/lib/components/InfoTooltipIcon";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import {Constants} from "@/api/constants";
import {useTranslation} from "react-i18next";
import { CommonI18N, RequestI18N } from "@/translations";
import {OrderDataType} from "@/model/orderData";
import {useCardChangeColors} from "@/common/request-cards/common";
import {MILLIMETRE_UNIT} from "@/constants";
import CurrencyInputField from "@/lib/components/inputfields/currency-input-field";

export type PalletizingCardProps = {
    orderData?: OrderDataDTO;
    onChange: ChangeHandler;
    errors?: ErrorsShape<{ orderData?: OrderDataDTO }>;
    mode: OrderDataType;
    readonly?: boolean;
    orgData?: OrderDataDTO | null;
};

export const PalletizingCard = ({ orderData, orgData, errors, onChange, readonly, mode }: PalletizingCardProps) => {
    const { t } = useTranslation();
    const { getGridChangeSx } = useCardChangeColors();
    const palletTypeSelectOptions = palletTypeOptions.map((option) => ({
        label: option && t(CommonI18N.enum.palletType[option]),
        value: option
    }));
    const numberOfPalletLabelOptions = numberOfPalletLabels.map((option) => ({
        label: option && t(CommonI18N.enum.numberOfPallet[option]),
        value: option
    }));
    const readonlyEx = readonly || mode === "quote";

    const helperTextPalletType =
        orgData && orgData?.palletType !== orderData?.palletType ? t(CommonI18N.enum.palletType[orgData?.palletType]) : undefined;

    const helperTextPalletHeight =
        orgData && orgData?.palletHeight !== orderData?.palletHeight ? orgData?.palletHeight : undefined;

    return (
        <CardEx title={t(RequestI18N.details.page.palletizingCardTitle)}>
            <Grid container gap={2} direction="column">
                <Grid width="100%" container direction="row" spacing={1}>
                    <Grid size={{ xs: 12, sm: 8 }} sx={getGridChangeSx(orgData, helperTextPalletType)}>
                        <SelectInputField
                            name="orderData.palletType"
                            label={t(RequestI18N.details.field.palletType)}
                            onChange={onChange}
                            value={orderData?.palletType ?? ""}
                            error={errors?.orderData?.palletType}
                            options={palletTypeSelectOptions}
                            disabled={readonly}
                            helperText={helperTextPalletType}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }} sx={getGridChangeSx(orgData, helperTextPalletHeight)}>
                        <NumberInputField
                            name="orderData.palletHeight"
                            label={t(RequestI18N.details.field.palletHeight)}
                            onChange={onChange}
                            value={orderData?.palletHeight}
                            error={errors?.orderData?.palletHeight}
                            suffix={` ${MILLIMETRE_UNIT}`}
                            endAdornment={
                                <InputAdornment position="end">
                                    <InfoTooltipIcon tooltipText={t(RequestI18N.details.tooltip.palletHeight)}/>
                                </InputAdornment>
                            }
                            min={Constants.PALLET_HEIGHT_MIN}
                            max={Constants.PALLET_HEIGHT_MAX}
                            helperText={helperTextPalletHeight}
                            disabled={readonly}
                        />
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12 }} container direction="row" spacing={1}>
                    <Grid
                        size={{ xs: 12, sm: 4 }}
                        sx={getGridChangeSx(orgData, orgData?.pricePerPallet !== orderData?.pricePerPallet)}
                    >
                        <CurrencyInputField
                            name="orderData.pricePerPallet"
                            label={t(RequestI18N.details.field.pricePerPallet)}
                            onChange={onChange}
                            value={orderData?.pricePerPallet}
                            error={errors?.orderData?.pricePerPallet}
                            min={Constants.PRICE_PER_PALLET_MIN}
                            max={Constants.PRICE_PER_PALLET_MAX}
                            disabled={readonly}
                            helperText={
                                orgData && orgData?.pricePerPallet !== orderData?.pricePerPallet
                                    ? orgData?.pricePerPallet
                                    : undefined
                            }
                        />
                    </Grid>
                    <Grid
                        size={{ xs: 12, sm: 4 }}
                        sx={getGridChangeSx(orgData, orgData?.quantityPerPallet !== orderData?.quantityPerPallet)}
                    >
                        <NumberInputField
                            name="orderData.quantityPerPallet"
                            label={t(RequestI18N.details.field.quantityPerPallet)}
                            onChange={onChange}
                            value={orderData?.quantityPerPallet}
                            error={errors?.orderData?.quantityPerPallet}
                            min={Constants.QUANTITY_PER_PALLET_MAX}
                            max={Constants.QUANTITY_PER_PALLET_MAX}
                            disabled={readonly}
                            helperText={
                                orgData && orgData?.quantityPerPallet !== orderData?.quantityPerPallet
                                    ? orgData?.quantityPerPallet
                                    : undefined
                            }
                        />
                    </Grid>
                    <Grid
                        size={{ xs: 12, sm: 4 }}
                        sx={getGridChangeSx(orgData, orgData?.bundlesPerPackage !== orderData?.bundlesPerPackage)}
                    >
                        <NumberInputField
                            name="orderData.bundlesPerPackage"
                            label={t(RequestI18N.details.field.bundlesPerPackage)}
                            onChange={onChange}
                            value={orderData?.bundlesPerPackage}
                            error={errors?.orderData?.bundlesPerPackage}
                            min={Constants.BUNDLES_PER_PACKAGE_MIN}
                            max={Constants.BUNDLES_PER_PACKAGE_MAX}
                            disabled={readonly}
                            helperText={
                                orgData && orgData?.bundlesPerPackage !== orderData?.bundlesPerPackage
                                    ? orgData?.bundlesPerPackage
                                    : undefined
                            }
                        />
                    </Grid>
                </Grid>
                <Grid size={{ sm: 12 }} paddingLeft={1} container direction="row" spacing={1}>
                    <Grid>
                        <CheckboxInputField
                            name="orderData.counterPallets"
                            label={t(RequestI18N.details.field.counterPallets)}
                            onChange={onChange}
                            value={orderData?.counterPallets}
                            error={errors?.orderData?.counterPallets}
                            dense
                            disabled={readonlyEx}
                        />
                    </Grid>
                    <Grid>
                        <CheckboxInputField
                            name="orderData.palletExchange"
                            label={t(RequestI18N.details.field.palletExchange)}
                            onChange={onChange}
                            value={orderData?.palletExchange}
                            error={errors?.orderData?.palletExchange}
                            dense
                            disabled={readonlyEx}
                        />
                    </Grid>
                    <Grid>
                        <CheckboxInputField
                            name="orderData.palletLabelNeutral"
                            label={t(RequestI18N.details.field.palletLabelNeutral)}
                            onChange={onChange}
                            value={orderData?.palletLabelNeutral}
                            error={errors?.orderData?.palletLabelNeutral}
                            dense
                            disabled={readonlyEx}
                        />
                    </Grid>
                    <Grid>
                        <CheckboxInputField
                            name="orderData.stretching"
                            label={t(RequestI18N.details.field.stretching)}
                            onChange={onChange}
                            value={orderData?.stretching}
                            error={errors?.orderData?.stretching}
                            dense
                            disabled={readonlyEx}
                        />
                    </Grid>
                </Grid>
                <Grid size={{ sm: 12 }} paddingLeft={1} container direction="row" spacing={1} flexWrap="nowrap">
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <CheckboxInputField
                            name="orderData.allowPalletOverhang"
                            label={t(RequestI18N.details.field.allowPalletOverhang)}
                            onChange={onChange}
                            value={orderData?.allowPalletOverhang}
                            error={errors?.orderData?.allowPalletOverhang}
                            dense
                            disabled={readonlyEx}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <NumberInputField
                            name="orderData.palletOverhang"
                            label={t(RequestI18N.details.field.palletOverhang)}
                            onChange={onChange}
                            value={orderData?.palletOverhang}
                            error={errors?.orderData?.palletOverhang}
                            suffix={` ${MILLIMETRE_UNIT}`}
                            min={Constants.PALLET_OVERHANG_MIN}
                            max={Constants.PALLET_OVERHANG_MAX}
                            disabled={readonlyEx || !orderData?.allowPalletOverhang}
                        />
                    </Grid>
                </Grid>
                <Grid size={{ sm: 12 }} paddingLeft={1} container direction="row" spacing={1}>
                    <Grid size={{ sm: 8 }}>
                        <SelectInputField
                            name="orderData.numberOfPalletLabel"
                            label={t(RequestI18N.details.field.numberOfPallet)}
                            onChange={onChange}
                            value={orderData?.numberOfPalletLabel ?? ""}
                            error={errors?.orderData?.numberOfPalletLabel}
                            options={numberOfPalletLabelOptions}
                            endAdornment={
                                <InfoTooltipIcon tooltipText={t(RequestI18N.details.tooltip.numberOfPallet)}/>
                            }
                            disabled={readonlyEx}
                        />
                    </Grid>
                    <Grid size={{ sm: 4 }} paddingTop="2px">
                        <CheckboxInputField
                            name="orderData.palletizingFixed"
                            label={t(RequestI18N.details.field.palletizingFixed)}
                            onChange={onChange}
                            value={orderData?.palletizingFixed}
                            error={errors?.orderData?.palletizingFixed}
                            dense
                            disabled={readonlyEx}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </CardEx>
    );
};
